import { useEffect, useRef } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '../../ui/button';
import type { InvestmentProperty, InvestmentResult } from '../InvestmentFlow.types';
import { formatCurrency } from '../InvestmentFlow.utils';

interface CompleteStepProps {
  property: InvestmentProperty;
  result: InvestmentResult;
  onViewPortfolio: () => void;
  onBrowseMore: () => void;
}

export function CompleteStep({ property, result, onViewPortfolio, onBrowseMore }: CompleteStepProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    // role="alert" causes screen readers to announce the completion immediately
    <div role="alert" className="text-center space-y-8">
      <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto">
        <CheckCircle2 className="w-10 h-10 text-success" aria-hidden="true" />
      </div>

      <div className="space-y-2">
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-3xl md:text-4xl focus-visible:outline-none"
        >
          You're all set!
        </h1>
        <p className="text-lg text-muted-foreground">
          Your {formatCurrency(result.amount)} investment in{' '}
          <span className="font-medium text-foreground">{property.title}</span> is confirmed.
        </p>
      </div>

      {/* Transaction summary */}
      <dl className="rounded-xl border-2 border-border p-6 bg-card text-left divide-y divide-border">
        <div className="flex items-center justify-between py-2">
          <dt className="text-sm text-muted-foreground">Confirmation ID</dt>
          <dd className="font-mono text-sm">{result.transactionId.slice(0, 8).toUpperCase()}</dd>
        </div>
        <div className="flex items-center justify-between py-2">
          <dt className="text-sm text-muted-foreground">Shares acquired</dt>
          <dd className="font-medium">{result.shares.toLocaleString()}</dd>
        </div>
        <div className="flex items-center justify-between py-2">
          <dt className="text-sm text-muted-foreground">Amount invested</dt>
          <dd className="font-medium">{formatCurrency(result.amount)}</dd>
        </div>
      </dl>

      {/* What happens next */}
      <div className="rounded-xl border-2 border-border p-6 bg-card text-left space-y-4">
        <h2 className="font-medium text-lg">What happens next</h2>
        <ol className="space-y-3 list-none p-0">
          {[
            {
              label: 'Confirmation email on its way',
              detail: 'Within the next few minutes',
            },
            {
              label: 'Shares added to your portfolio',
              detail: 'After ACH transfer clears (3–5 business days)',
            },
            {
              label: 'Start earning returns',
              detail: 'Quarterly dividends begin next quarter',
            },
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-medium text-primary"
              >
                {i + 1}
              </span>
              <div>
                <p className="font-medium">{item.label}</p>
                <p className="text-sm text-muted-foreground">{item.detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="space-y-3">
        <Button onClick={onViewPortfolio} className="w-full h-14 text-lg">
          View My Portfolio
        </Button>
        <Button variant="outline" onClick={onBrowseMore} className="w-full h-14 text-lg">
          Browse More Properties
        </Button>
      </div>
    </div>
  );
}
